import { useEditorModel } from '@/store/models/editor-model';
import './layer-item.less';
import classnames from 'classnames';
import { cloneDeep } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Popover, Tooltip } from '@meitu/saas-ui';
import { base64Kit } from '@meitu/saas-editor-core/es/utils/base64-kit';
import { getSysType, SYSTEM_TYPE } from '@meitu/saas-editor-core/es/utils/platform-kit';
import { traverseTree } from '@meitu/saas-editor-core/es/utils/tree-kit';
import {
    IBrushLayerProps,
    IImageLayerProps,
    ILayerProps,
    ITableLayerProps,
    ITextLayerProps,
    LayerType,
} from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { useTypo } from '@/hooks/use-typo';
import { editor } from '@/instance';
import { extractEnglishWords } from '@/utils';
import { imgModeMap, reverseLayers } from './utils';

const system = getSysType();
const prefix = system === SYSTEM_TYPE.MAC ? 'mac' : 'win';

const keys = [
    {
        name: '组合',
        win: 'Ctrl + G',
        mac: '⌘ + G',
        key: 'makeGroup',
    },
].map((el) => {
    return {
        ...el,
        keyName: el[prefix],
    };
});

const ContextMenu: React.FC<{ handleClick: Function }> = ({ handleClick }) => {
    return (
        <ul className="layer-context-menu">
            {keys.map((item, index) => {
                return (
                    <li className="layer-context-menu__item" key={index} onClick={() => handleClick(item.key)}>
                        <div className="l-content-menu__item__title">
                            <span>{item.name}</span>
                        </div>
                        <span>{item.keyName}</span>
                    </li>
                );
            })}
        </ul>
    );
};

const LayerIcon: React.FC<{ url: string }> = React.memo(({ url }) => {
    const [imgSrc, setImgSrc] = useState('');
    useEffect(() => {
        const renderUrl = base64Kit.getLocalUrl(url);
        setImgSrc(renderUrl);
    }, [url]);
    return imgSrc ? <img src={imgSrc} style={{ width: 16, height: 16, objectFit: 'contain' }} alt="" /> : null;
});

/**
 * 在图层列表中展示并操作条目，依赖画布图层数据与英文拼写检测状态
 *
 * 操作会修改画布图层、历史记录和选中态。
 *
 * @param props - 图层列表快照、父层可见性和选择操作回调
 * @returns 图层条目及其重命名、组合和可见性操作入口
 */
const LayerItem: React.FC<{
    layer: ILayerProps;
    setDisableOperate: (disable: boolean) => void;
    parentIsVisible: boolean;
    selectIds: string[];
    selectLayer: Function;
    handleMakeGroup: Function;
    activeLayerId: string | null;
}> = ({ layer, setDisableOperate, parentIsVisible, selectIds, selectLayer, handleMakeGroup, activeLayerId }) => {
    const { layerType, title: layerTitle } = layer;
    let iconElem;
    let title = layerTitle;
    const [currentEditTitle, setCurrentEditTitle] = useState('');
    const { check } = useTypo();
    const [isTypo, setIsTypo] = useState(false);
    const [errorWords, setErrorWords] = useState<string[]>([]);
    const isShowTypo = useEditorModel((state) => state.isShowTypo);
    useEffect(() => {
        if (layerType === LayerType.TEXT_LAYER && (layer.data as ITextLayerProps).text && isShowTypo) {
            const text = (layer.data as ITextLayerProps).text;
            // 提取英文，并且不包含标点符号
            const enText = extractEnglishWords(text);
            const errorWordList: string[] = [];
            if (enText.length > 0) {
                enText.forEach((word) => {
                    const result = check(word);
                    if (!result) {
                        errorWordList.push(word);
                    }
                });
            }
            setErrorWords(errorWordList);
        }
    }, [layer, isShowTypo]);

    useEffect(() => {
        setIsTypo(errorWords.length > 0);
        // console.log('errorWords==', errorWords);
    }, [errorWords]);

    switch (layerType) {
        case LayerType.BACKGROUND_LAYER: {
            title = layer?.title || '背景';
            iconElem = '';
            break;
        }
        case LayerType.TEXT_LAYER: {
            const { text } = layer.data as ITextLayerProps;
            title = title || text;
            iconElem = <IconFont name="TextSquare" fontSize={16} />;
            break;
        }

        case LayerType.IMAGE_LAYER: {
            const { url } = layer.data as IImageLayerProps;
            title = layer?.title || '图案';
            iconElem = <LayerIcon url={url} />;
            break;
        }
        case LayerType.BRUSH_LAYER: {
            const { screenshotUrl: brushScreenshotUrl } = layer.data as IBrushLayerProps;
            title = layer?.title || '形状';
            iconElem = brushScreenshotUrl ? (
                <LayerIcon url={brushScreenshotUrl} />
            ) : (
                <IconFont name="BrushAndBoard" fontSize={16} />
            );
            break;
        }
        case LayerType.TABLE: {
            const { screenshotUrl: tableScreenshotUrl } = layer.data as ITableLayerProps;
            title = layer?.title || '表格';
            iconElem = tableScreenshotUrl ? (
                <LayerIcon url={tableScreenshotUrl} />
            ) : (
                <IconFont name="TabulationBold" fontSize={16} />
            );
            break;
        }
        case LayerType.GROUP_LAYER: {
            title = layer?.title || '图层分组';
            iconElem = (
                <div className="c-layer-list-item-icon-group">
                    <IconFont
                        name={layer.extraInfo?.isExpanded ? 'ChevronDownBold' : 'ChevronRightBold'}
                        fontSize={12}
                        onClick={() => {
                            if (!layer.extraInfo) {
                                layer.extraInfo = {};
                            }
                            layer.extraInfo.isExpanded = !layer.extraInfo.isExpanded;
                            editor.services.frame.addOrUpdateLayer(reverseLayers([layer])[0]);
                        }}
                    />
                    <IconFont name="Folder" fontSize={16} />
                </div>
            );
            break;
        }
        case LayerType.VECTOR_LAYER: {
            title = `${layer?.title || '形状'}`;
            iconElem = editor.getBehavior(layer)?.getMenuOverlapIcon?.(layer);
            break;
        }
        case LayerType.QR_CODE: {
            title = `${layer?.title || '二维码'}`;
            iconElem = editor.getBehavior(layer)?.getMenuOverlapIcon?.(layer);
            break;
        }

        case LayerType.CHART: {
            title = layer?.title || '图表';
            iconElem = <IconFont name="StackedColumnGraph" fontSize={16} />;
            break;
        }

        case LayerType.MARKUP_LAYER: {
            title = layer?.title || '标注笔';
            iconElem = <IconFont name="RegionVary" fontSize={16} />;
            break;
        }

        case LayerType.MEDIA_LAYER: {
            title = layer?.title || '动态素材';
            iconElem = <IconFont name="LivePhotoPlay" fontSize={16} />;
            break;
        }
    }

    const handleClick = (key: string) => {
        switch (key) {
            case 'makeGroup':
                handleMakeGroup();
                break;
        }
    };

    return (
        <Popover
            content={selectIds.includes(layer.id) ? <ContextMenu handleClick={handleClick} /> : null}
            trigger="contextMenu"
            placement="rightTop"
            overlayClassName="layer-context-menu-popover "
        >
            <div
                className={classnames('c-layer-list-item', {
                    active: layer.id === activeLayerId && selectIds.includes(layer.id),
                    'temp-active': selectIds.includes(layer.id) && layer.id !== activeLayerId,
                })}
                onClick={() => {
                    // 编辑器的子图层组是无法选中的
                    if (layer.hidden || !parentIsVisible) {
                        return;
                    }
                    selectLayer(layer);
                    if (layer.layerType === LayerType.GROUP_LAYER && layer.parentId) {
                        return;
                    }
                    editor.services.frame.setActiveLayer(layer.id);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Delete') {
                        editor.layer.delete(layer.id);
                    }
                }}
            >
                <div className="c-layer-list-item__left">
                    <span
                        className="c-layer-list-item-icon"
                        style={{
                            height: '16px',
                            width: layer.layerType === LayerType.GROUP_LAYER ? '40px' : '16px',
                        }}
                    >
                        {iconElem}
                    </span>
                    {layer.extraInfo?.isEditTitle ? (
                        <input
                            type="text"
                            value={currentEditTitle}
                            className="c-layer-list-item-title-input"
                            onChange={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setCurrentEditTitle(e.target.value);
                            }}
                            onBlur={() => {
                                layer.title = currentEditTitle;
                                editor.imageLayer.updateLayer(
                                    cloneDeep(
                                        reverseLayers([
                                            {
                                                ...layer,
                                                extraInfo: { ...(layer.extraInfo || {}), isEditTitle: false },
                                            },
                                        ])[0],
                                    ),
                                    cloneDeep(reverseLayers([layer])[0]),
                                    true,
                                );
                                editor.services.frame.setActiveLayer(layer.id);
                                setDisableOperate(false);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    layer.title = currentEditTitle;
                                    editor.imageLayer.updateLayer(
                                        cloneDeep(
                                            reverseLayers([
                                                {
                                                    ...layer,
                                                    extraInfo: { ...(layer.extraInfo || {}), isEditTitle: false },
                                                },
                                            ])[0],
                                        ),
                                        cloneDeep(reverseLayers([layer])[0]),
                                        true,
                                    );
                                    setDisableOperate(false);
                                    editor.services.frame.setActiveLayer(layer.id);
                                }
                            }}
                        />
                    ) : (
                        <span
                            className="c-layer-list-item-title"
                            onDoubleClick={() => {
                                if (!layer.extraInfo) {
                                    layer.extraInfo = {};
                                }
                                layer.extraInfo.isEditTitle = true;
                                setCurrentEditTitle(layerTitle);
                                editor.services.frame.addOrUpdateLayer(cloneDeep(reverseLayers([layer])[0]));
                                setDisableOperate(true);
                            }}
                        >
                            {layer.mode ? (
                                <span style={{ color: 'red' }}>{imgModeMap[layer.mode]?.slice(2)} </span>
                            ) : null}
                            {`${title}${layer.layerType === LayerType.VECTOR_LAYER || layer.layerType === LayerType.BRUSH_LAYER ? ' (svg)' : ''}`}
                        </span>
                    )}
                </div>
                <div className="c-layer-list-item__right">
                    {isTypo && isShowTypo ? (
                        <Tooltip title={`存在可能拼写错误：【 ${errorWords} 】请检查`}>
                            <IconFont name="InfoCircleFill" fontSize={12} color="#ff9800" />
                        </Tooltip>
                    ) : null}
                    {layer.lock ? <IconFont name="LockFill" fontSize={12} color="#ff2200" /> : null}
                    {parentIsVisible ? (
                        <IconFont
                            name={layer.hidden ? 'InvisibleBold' : 'VisibleBold'}
                            onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                // 列表快照可能尚未同步拖动后的坐标，提交时必须读取最新图层
                                const updatedLayer = await editor.imageLayer.updateLayerFromLatest(
                                    layer.id,
                                    (latestLayer) => {
                                        const hidden = !latestLayer.hidden;
                                        traverseTree(latestLayer, (child: ILayerProps) => {
                                            child.hidden = hidden;
                                        });
                                        return latestLayer;
                                    },
                                    { activateLayer: false },
                                );
                                if (updatedLayer) {
                                    editor.services.frame.delActiveLayer();
                                }
                            }}
                            fontSize={12}
                        />
                    ) : null}
                </div>
            </div>
        </Popover>
    );
};

export default LayerItem;
